/**
 * OpsRetrievalProgress
 *
 * Polls /api/ops-retrieval/jobs/{jobId} every 3 s and shows a live progress view.
 * On completion redirects to the search-session list for the sub-project.
 * User can cancel the job from this page.
 */

import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { opsRetrievalService, formatDuration, type OpsRetrievalJob } from '../services/opsRetrievalService';

const POLL_INTERVAL_MS = 3000;

export default function OpsRetrievalProgress() {
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [job, setJob] = useState<OpsRetrievalJob | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const [relinking, setRelinking] = useState(false);
  const [relinkResult, setRelinkResult] = useState<string | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchJob = async () => {
    if (!jobId) return;
    try {
      const data = await opsRetrievalService.getJobStatus(jobId);
      setJob(data);
      if (['completed', 'failed', 'cancelled'].includes(data.status)) {
        stopPolling();
      }
    } catch (err: unknown) {
      const e = err as { response?: { data?: { detail?: string } }; message?: string };
      setError(e?.response?.data?.detail ?? e?.message ?? 'Failed to load job');
      stopPolling();
    }
  };

  const startPolling = () => {
    fetchJob();
    intervalRef.current = setInterval(fetchJob, POLL_INTERVAL_MS);
  };

  const stopPolling = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  useEffect(() => {
    startPolling();
    return stopPolling;
  }, [jobId]);

  const handleCancel = async () => {
    if (!jobId) return;
    setCancelling(true);
    try {
      await opsRetrievalService.cancelJob(jobId);
    } catch (err: unknown) {
      const e = err as { response?: { data?: { detail?: string } }; message?: string };
      setError(e?.response?.data?.detail ?? e?.message ?? 'Cancel failed');
    } finally {
      setCancelling(false);
    }
  };

  const handleRelinkKb = async () => {
    if (!jobId) return;
    setRelinking(true);
    setRelinkResult(null);
    try {
      const result = await opsRetrievalService.relinkJobToKb(jobId);
      setRelinkResult(t('opsRetrieval.progress.relinkSuccess', '{{count}} documents linked to KB.', { count: result.newly_linked }));
    } catch (err: unknown) {
      const e = err as { response?: { data?: { detail?: string } }; message?: string };
      setRelinkResult(t('opsRetrieval.progress.relinkError', 'Re-link failed: {{msg}}', { msg: e?.response?.data?.detail ?? e?.message ?? 'Unknown error' }));
    } finally {
      setRelinking(false);
    }
  };

  const handleViewResults = () => {
    if (!job) return;
    // Navigate to the sub-project page; from there the user can open the session
    navigate(`/scv2/sub-project/${job.sub_project_id}`);
  };

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <p className="text-sm text-red-600">{error}</p>
        <button className="text-sm text-slate-500 hover:underline" onClick={() => navigate(-1)}>
          {t('common.back', 'Back')}
        </button>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-sm text-slate-500">{t('common.loading', 'Loading…')}</p>
      </div>
    );
  }

  const { progress, status, mode, estimated_duration_seconds, cql, error_details } = job;
  const { total_patents, processed_patents, failed_patents, current_patent_id } = progress;
  const targetKbId = job.target_kb_id;
  const googlePatentsFailures = (progress as unknown as Record<string, unknown>).google_patents_failures as number ?? 0;
  const textSource = (job as unknown as Record<string, unknown>).text_source as string ?? 'ops';
  const pct = total_patents > 0 ? Math.round((processed_patents / total_patents) * 100) : 0;

  const elapsedEstimate = (() => {
    if (!job.started_at) return null;
    const elapsed = (Date.now() - new Date(job.started_at).getTime()) / 1000;
    const remaining = estimated_duration_seconds - elapsed;
    return remaining > 0 ? remaining : 0;
  })();

  const statusLabel: Record<OpsRetrievalJob['status'], string> = {
    pending: t('opsRetrieval.progress.pending', 'Pending…'),
    running: t('opsRetrieval.progress.running', 'Running'),
    completed: t('opsRetrieval.progress.completed', 'Completed'),
    failed: t('opsRetrieval.progress.failed', 'Failed'),
    cancelled: t('opsRetrieval.progress.cancelled', 'Cancelled'),
    cancelling: t('opsRetrieval.progress.cancelling', 'Cancelling…'),
  };

  const isActive = status === 'running' || status === 'pending';
  const isDone = status === 'completed';
  const isError = status === 'failed';

  return (
    <div className="max-w-2xl mx-auto px-4 py-8 space-y-6">
      <h1 className="text-lg font-semibold text-slate-800">
        {t('opsRetrieval.progress.title', 'OPS Patent Retrieval')}
        {' — '}
        <span className={`text-sm font-normal ${isError ? 'text-red-600' : isDone ? 'text-green-600' : 'text-slate-500'}`}>
          {statusLabel[status]}
        </span>
      </h1>

      {/* Mode badge */}
      <div className="flex items-center gap-3 text-xs text-slate-500">
        <span className="bg-slate-100 rounded px-2 py-0.5">
          {mode === 'full' ? t('opsRetrieval.progress.modeFull', 'Full text') : t('opsRetrieval.progress.modeList', 'List + biblio')}
        </span>
        {mode === 'full' && (
          <span className={`rounded px-2 py-0.5 ${textSource === 'google_patents' ? 'bg-green-50 text-green-700' : 'bg-slate-100 text-slate-600'}`}>
            {textSource === 'google_patents' ? 'Google Patents' : 'EPO OPS'}
          </span>
        )}
        {elapsedEstimate !== null && isActive && (
          <span>{t('opsRetrieval.progress.eta', 'ETA')}: ~{formatDuration(elapsedEstimate)}</span>
        )}
      </div>

      {/* Progress bar */}
      <div>
        <div className="flex justify-between text-xs text-slate-500 mb-1">
          <span>{processed_patents.toLocaleString()} / {total_patents.toLocaleString()} {t('opsRetrieval.progress.patents', 'patents')}</span>
          <span>{pct}%</span>
        </div>
        <div className="w-full bg-slate-200 rounded-full h-3">
          <div
            className={`h-3 rounded-full transition-all ${isError ? 'bg-red-400' : isDone ? 'bg-green-500' : 'bg-blue-500'}`}
            style={{ width: `${pct}%` }}
          />
        </div>
        {failed_patents > 0 && (
          <p className="text-xs text-amber-600 mt-1">
            {failed_patents} {t('opsRetrieval.progress.failedPatents', 'patents could not be retrieved')}
          </p>
        )}
        {googlePatentsFailures > 0 && (
          <p className="text-xs text-slate-500 mt-1">
            {googlePatentsFailures} {t('opsRetrieval.progress.gpFallback', 'patents retrieved via OPS fallback')}
          </p>
        )}
        {current_patent_id && isActive && (
          <p className="text-xs text-slate-400 mt-1">{t('opsRetrieval.progress.current', 'Processing')}: {current_patent_id}</p>
        )}
      </div>

      {/* CQL summary */}
      <details className="text-xs">
        <summary className="cursor-pointer text-slate-500 hover:text-slate-700">
          {t('opsRetrieval.progress.showCql', 'Show CQL query')}
        </summary>
        <pre className="mt-1 p-2 bg-slate-50 border border-slate-200 rounded font-mono whitespace-pre-wrap break-all text-slate-600">{cql}</pre>
      </details>

      {/* Error details */}
      {isError && error_details && (
        <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded p-2">{error_details}</p>
      )}

      {/* Re-link feedback */}
      {relinkResult && (
        <p className={`text-xs rounded p-2 ${relinkResult.includes('failed') || relinkResult.includes('Error') ? 'text-red-600 bg-red-50 border border-red-200' : 'text-green-700 bg-green-50 border border-green-200'}`}>
          {relinkResult}
        </p>
      )}

      {/* Actions */}
      <div className="flex gap-3 flex-wrap">
        {isDone && (
          <button
            className="px-4 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
            onClick={handleViewResults}
          >
            {t('opsRetrieval.progress.viewResults', 'View Results')}
          </button>
        )}
        {isDone && targetKbId && (
          <button
            className="px-4 py-2 text-sm bg-amber-100 text-amber-800 rounded hover:bg-amber-200 disabled:opacity-50"
            disabled={relinking}
            onClick={handleRelinkKb}
            title={t('opsRetrieval.progress.relinkTitle', 'Re-create KB links if the KB appears empty')}
          >
            {relinking
              ? t('opsRetrieval.progress.relinking', 'Linking…')
              : t('opsRetrieval.progress.relinkBtn', 'Re-link to KB')}
          </button>
        )}
        {isActive && (
          <button
            className="px-4 py-2 text-sm bg-red-100 text-red-700 rounded hover:bg-red-200 disabled:opacity-50"
            disabled={cancelling || job.status === 'cancelling'}
            onClick={handleCancel}
          >
            {cancelling || job.status === 'cancelling'
              ? t('opsRetrieval.progress.cancelling', 'Cancelling…')
              : t('opsRetrieval.progress.cancelBtn', 'Cancel')}
          </button>
        )}
        <button
          className="px-4 py-2 text-sm bg-slate-100 text-slate-700 rounded hover:bg-slate-200"
          onClick={() => navigate(-1)}
        >
          {t('common.back', 'Back')}
        </button>
      </div>
    </div>
  );
}
